import { useEffect, useState } from "react";
import type { Difficulty, Puzzle } from "../shared/nonogram/types";
import { PuzzleExperience } from "./components/PuzzleExperience";
import { SiteFooter } from "./components/SiteFooter";
import { SiteHeader } from "./components/SiteHeader";
import { SeoContent } from "./components/SeoContent";
import { dailyDatePageFor } from "../shared/seo/dailyDate";
import { seoPageForPath } from "../shared/seo/pages";
import { fetchDailyPuzzle, fetchRandomPuzzle } from "./lib/api";
import { track } from "./lib/analytics";
import { loadStats, recordVisit } from "./lib/storage";
import { HowToPlay } from "./pages/HowToPlay";
import { Techniques } from "./pages/Techniques";
import { About } from "./pages/About";
import { Contact } from "./pages/Contact";
import { Privacy } from "./pages/Privacy";
import { Terms } from "./pages/Terms";
import { Archive } from "./pages/Archive";
import { Collection } from "./pages/Collection";
import { lazy, Suspense } from "react";

// The ternary is what keeps the answer key out of production, not the route check:
// with `import.meta.env.DEV` folded to false, the dynamic import is unreachable and
// Rollup drops the chunk instead of emitting it for anyone to fetch by URL.
const ArtReview = import.meta.env.DEV
  ? lazy(() => import("./pages/ArtReview").then((module) => ({ default: module.ArtReview })))
  : undefined;
const ArtStudio = import.meta.env.DEV
  ? lazy(() => import("./pages/ArtStudio").then((module) => ({ default: module.ArtStudio })))
  : undefined;
import { Printable } from "./pages/Printable";
import { PRINTABLE_PATHS } from "../shared/seo/pages";

// The grand sizes play exactly like a standard grid — same restore rules, no
// streak — so they ride the standard mode and carry only their own size.
type PlayRoute =
  | { mode: "daily"; seoPath: "/" | "/daily-nonogram"; date?: string }
  | { mode: "standard"; difficulty: Difficulty; seoPath: StandardSeoPath }
  | { mode: "grand"; difficulty: Difficulty; size: 20 | 25; seoPath: "/nonogram/20x20" | "/nonogram/25x25" };

type StandardSeoPath = "/nonogram" | "/nonogram/easy" | "/nonogram/medium" | "/nonogram/hard";
type StaticRoute = "how-to" | "techniques" | "techniques-overlap" | "techniques-edge" | "techniques-forcing" | "techniques-gaps" | "about" | "contact" | "terms" | "privacy" | "archive" | "collection" | "printable" | "art-review" | "art-studio" | "not-found";

function routeFor(pathname: string): PlayRoute | StaticRoute {
  if (pathname === "/how-to-play-nonogram") return "how-to";
  if (pathname === "/nonogram/techniques") return "techniques";
  if (pathname === "/nonogram/techniques/overlap") return "techniques-overlap";
  if (pathname === "/nonogram/techniques/edge") return "techniques-edge";
  if (pathname === "/nonogram/techniques/forcing") return "techniques-forcing";
  if (pathname === "/nonogram/techniques/gaps") return "techniques-gaps";
  if (pathname === "/about") return "about";
  if (pathname === "/contact") return "contact";
  if (pathname === "/terms") return "terms";
  if (pathname === "/privacy") return "privacy";
  if (pathname === "/daily-nonogram/archive") return "archive";
  if (pathname === "/collection") return "collection";
  // A complete answer key: it exists while developing and nowhere else. Vite folds
  // `import.meta.env.DEV` to false for production, so both this branch and the lazy
  // import below are dropped from the shipped bundle.
  if (import.meta.env.DEV && pathname === "/dev/art") return "art-review";
  if (import.meta.env.DEV && (pathname === "/dev/studio" || pathname === "/studio")) return "art-studio";
  if (PRINTABLE_PATHS.has(pathname)) return "printable";
  if (pathname === "/") return { mode: "daily", seoPath: "/" };
  if (pathname === "/daily-nonogram") return { mode: "daily", seoPath: "/daily-nonogram" };
  // Every published date has its own address. The server renders that page's copy;
  // this keeps the board on it behaving like any other daily.
  const dated = /^\/daily-nonogram\/(\d{4}-\d{2}-\d{2})$/.exec(pathname);
  if (dated?.[1]) return { mode: "daily", seoPath: "/daily-nonogram", date: dated[1] };
  if (pathname === "/nonogram/20x20") return { mode: "grand", difficulty: "hard", size: 20, seoPath: "/nonogram/20x20" };
  if (pathname === "/nonogram/25x25") return { mode: "grand", difficulty: "hard", size: 25, seoPath: "/nonogram/25x25" };
  if (pathname === "/nonogram/easy") return { mode: "standard", difficulty: "easy", seoPath: "/nonogram/easy" };
  if (pathname === "/nonogram/hard") return { mode: "standard", difficulty: "hard", seoPath: "/nonogram/hard" };
  if (pathname === "/nonogram") return { mode: "standard", difficulty: "medium", seoPath: "/nonogram" };
  if (pathname === "/nonogram/medium") return { mode: "standard", difficulty: "medium", seoPath: "/nonogram/medium" };
  return "not-found";
}

function LoadingPuzzle() {
  return (
    <main className="loading-page" id="main-content" aria-busy="true">
      <p className="eyebrow">Setting the clues</p>
      <h1>Your puzzle is almost ready</h1>
      <div className="loading-grid" aria-hidden="true">
        {Array.from({ length: 25 }, (_, index) => (
          <i key={index} style={{ animationDelay: `${index * 48}ms` }} />
        ))}
      </div>
    </main>
  );
}

export default function App() {
  const pathname = window.location.pathname.replace(/\/$/, "") || "/";
  const route = routeFor(pathname);
  const [puzzle, setPuzzle] = useState<Puzzle>();
  const [dailyDate, setDailyDate] = useState<string>();
  const [nextPuzzleAt, setNextPuzzleAt] = useState<string>();
  const [error, setError] = useState<string>();
  const [requestVersion, setRequestVersion] = useState(0);

  useEffect(() => {
    track("landing_view", { route: window.location.pathname, referrer_group: document.referrer ? "referral" : "direct" });
    const daysSinceLastVisit = recordVisit();
    if (daysSinceLastVisit !== undefined && daysSinceLastVisit > 0) {
      track("return_visit", { days_since_last_visit_bucket: Math.min(daysSinceLastVisit, 30) });
    }
  }, []);

  useEffect(() => {
    if (typeof route === "string") return undefined;
    const controller = new AbortController();
    const currentPuzzleId = puzzle?.id;
    setPuzzle(undefined);
    setError(undefined);
    void (async () => {
      try {
        if (route.mode === "daily") {
          const requestedDate = route.date ?? new URLSearchParams(window.location.search).get("date") ?? undefined;
          const response = await fetchDailyPuzzle(controller.signal, requestedDate);
          // A prefetched response cannot be aborted, so the guard is here rather than
          // left to fetch rejecting on the signal.
          if (controller.signal.aborted) return;
          setPuzzle(response.puzzle);
          setDailyDate(response.date);
          setNextPuzzleAt(response.nextPuzzleAt);
        } else {
          const excludeIds = Object.keys(loadStats().bestTimes);
          if (currentPuzzleId) excludeIds.push(currentPuzzleId);
          const response = await fetchRandomPuzzle(
            route.difficulty,
            controller.signal,
            excludeIds,
            route.mode === "grand" ? route.size : undefined,
          );
          if (controller.signal.aborted) return;
          setPuzzle(response.puzzle);
          setDailyDate(undefined);
          setNextPuzzleAt(undefined);
        }
      } catch (reason: unknown) {
        if (controller.signal.aborted || (reason instanceof DOMException && reason.name === "AbortError")) return;
        setError(reason instanceof Error ? reason.message : "We couldn't load this puzzle.");
      }
    })();
    return () => controller.abort();
  }, [requestVersion, typeof route === "string" ? route : `${route.mode}-${"difficulty" in route ? route.difficulty : route.date ?? "daily"}-${"size" in route ? route.size : ""}`]);

  /**
   * The build writes each page's <title> into its own static HTML and every link on
   * the site is a full page load, so production always arrives with the right one.
   * The dev server serves a single untemplated index.html for every route, which
   * makes every page look like the home page while you are working on it — and makes
   * a wrong title in the page definitions invisible until it is deployed.
   *
   * Reading it from the same definition the build reads is a no-op in production and
   * the truth in development. A dated archive page has no entry here; the worker
   * renders its title per request, so it is left alone.
   */
  useEffect(() => {
    const page = seoPageForPath(pathname);
    if (page && document.title !== page.title) document.title = page.title;
  }, [pathname]);

  let content;
  if (route === "how-to") content = <HowToPlay />;
  else if (route === "techniques") content = <Techniques path="/nonogram/techniques" />;
  else if (route === "techniques-overlap") content = <Techniques path="/nonogram/techniques/overlap" />;
  else if (route === "techniques-edge") content = <Techniques path="/nonogram/techniques/edge" />;
  else if (route === "techniques-forcing") content = <Techniques path="/nonogram/techniques/forcing" />;
  else if (route === "techniques-gaps") content = <Techniques path="/nonogram/techniques/gaps" />;
  else if (route === "about") content = <About />;
  else if (route === "contact") content = <Contact />;
  else if (route === "terms") content = <Terms />;
  else if (route === "privacy") content = <Privacy />;
  else if (route === "archive") content = <Archive />;
  else if (route === "collection") content = <Collection />;
  else if (route === "art-review") {
    content = ArtReview ? (
      <Suspense fallback={<main className="review-page" id="main-content"><p>Loading the art library…</p></main>}>
        <ArtReview />
      </Suspense>
    ) : null;
  }
  else if (route === "art-studio") {
    content = ArtStudio ? (
      <Suspense fallback={<main className="studio-page" id="main-content"><p>Loading Art Studio…</p></main>}>
        <ArtStudio />
      </Suspense>
    ) : null;
  }
  else if (route === "printable") content = <Printable path={pathname} />;
  else if (route === "not-found") {
    content = (
      <main className="error-page" id="main-content">
        <p className="eyebrow">404</p>
        <h1>This square is empty</h1>
        <p>The page you requested does not exist. Choose a puzzle and keep solving.</p>
        <a className="primary-button inline-button" href="/">Play today’s puzzle</a>
      </main>
    );
  }
  else if (error) {
    content = (
      <main className="error-page" id="main-content">
        <p className="eyebrow">Puzzle interrupted</p>
        <h1>We couldn’t set today’s grid</h1>
        <p>{error} Check your connection and try once more.</p>
        <button className="primary-button" type="button" onClick={() => setRequestVersion((value) => value + 1)}>Try again</button>
      </main>
    );
  } else if (!puzzle) content = <LoadingPuzzle />;
  else {
    // One source for the page's heading: whatever the server printed as the h1 is what
    // the app prints, on every route, finished or not.
    const seoPage = route.mode === "daily" && route.date
      ? dailyDatePageFor(puzzle, route.date)
      : seoPageForPath(route.seoPath);
    content = (
      <main className="play-page" id="main-content">
        <PuzzleExperience
          heading={seoPage?.heading}
          key={dailyDate ? `daily-${dailyDate}` : puzzle.id}
          puzzle={puzzle}
          mode={route.mode === "daily" ? "daily" : "standard"}
          dailyDate={route.mode === "daily" ? dailyDate : undefined}
          nextPuzzleAt={route.mode === "daily" ? nextPuzzleAt : undefined}
          onNewPuzzle={route.mode === "standard" ? () => setRequestVersion((value) => value + 1) : undefined}
        />
        {/* A dated archive page describes its own grid — the same words the server
            rendered — instead of the generic daily copy. */}
        {seoPage && route.mode === "daily" && route.date
          ? <SeoContent page={seoPage} />
          : <SeoContent path={route.seoPath} />}
      </main>
    );
  }

  return (
    <div className="site-frame">
      <SiteHeader />
      {content}
      <SiteFooter />
    </div>
  );
}
